import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { precheckPosFormSchema as validationSchema } from './validationSchema';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import i18n from '../../i18n';
import * as precheckService from '../../services/precheckService';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import BrandLogo from '../shared/BrandLogo';
import {
  FormField,
  FormSelect,
  FormCheckboxGroup,
  SectionHeader,
  ErrorSummary,
  SuccessMessage,
  ErrorMessage,
  SubmitButton,
} from '../form';
import { useScrollToError } from '../../hooks/useScrollToError';
import { parseApiError } from '../../api/apiError';

const key = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const PrecheckPOSForm: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [captchaReady, setCaptchaReady] = useState(false);
  const submittingRef = useRef(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const brand = queryParams.get('brand');
  const isPublicPath = location.pathname.includes('/p/');

  useEffect(() => {
    if (!isPublicPath) return;

    const markReady = () => {
      window.grecaptcha.enterprise.ready(() => setCaptchaReady(true));
    };

    if (window.grecaptcha?.enterprise) {
      markReady();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha-enterprise="true"]',
    );
    if (existing) {
      existing.addEventListener('load', markReady, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${key}`;
    script.dataset.recaptchaEnterprise = 'true';
    script.onload = markReady;
    document.body.appendChild(script);
  }, [isPublicPath]);

  const getCaptchaToken = async (): Promise<string> => {
    if (!captchaReady) {
      throw new Error('reCAPTCHA not ready');
    }
    return window.grecaptcha.enterprise.execute(key, { action: 'submit' });
  };

  const formik = useFormik({
    initialValues: {
      company: '',
      taxId: '',
      creditorId: '',
      industry: '',
      address: '',
      locations: '',
      website: '',
      director: '',
      contactPerson: '',
      email: '',
      telephone: '',
      mobile: '',
      onlineShop: '',
      currentProvider: '',
      turnover: '',
      receiptSize: '',
      bankStatementReference: '',
      terminals: 'no',
      checkoutConnection: 'no',
      cashCut: 'standard',
      tip: 'no',
      cards: [] as string[],
      additionalServices: [] as string[],
      sourceDomain: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      setErrorMessage('');
      setErrorDetails([]);

      if (isPublicPath && !captchaReady) {
        alert('reCAPTCHA is not ready yet. Please wait a moment and try again.');
        submittingRef.current = false;
        return;
      }

      try {
        const currentLanguage = i18n.language || 'en';
        values.sourceDomain = theme.domain; // Track which domain the submission came from

        if (isPublicPath) {
          const freshToken = await getCaptchaToken();
          await precheckService.submitPrecheckPosPublic(values, currentLanguage, freshToken);
        } else {
          await precheckService.submitPrecheckPos(values, currentLanguage);
        }

        resetForm();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSuccessMessage(t('common.formSubmittedSuccessfully'));
      } catch (error) {
        console.error('Error saving POS precheck form:', error);

        const { messages, fieldErrors } = parseApiError(error, {
          generic: t('common.unexpectedError'),
          network: t('common.networkError'),
        });

        // Surface the rejected fields inline as well as in the summary
        Object.entries(fieldErrors).forEach(([field, message]) => setFieldError(field, message));
        setErrorMessage(t('common.submissionFailed'));
        setErrorDetails(messages);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        submittingRef.current = false;
      }
    },
  });

  useScrollToError(formik.errors, formik.submitCount, formik.isSubmitting);

  const content = (
    <>
      <h2 className="text-4xl font-bold mb-8 text-slate-900">{t('prechecksPOS.formTitle')}</h2>

      <SuccessMessage message={successMessage} />

      <ErrorMessage message={errorMessage} details={errorDetails} />

      <form onSubmit={formik.handleSubmit} className="max-w-4xl space-y-8">
        <SectionHeader
          title={t('prechecksPOS.generalInformation')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          iconBgColor="bg-blue-100"
          iconTextColor="text-blue-600"
          titleColor="text-blue-600"
        >
          <FormField
            id="company"
            name="company"
            label={t('merchant.company')}
            type="text"
            value={formik.values.company}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.company}
            touched={formik.touched.company}
            required
            description={t('prechecksPOS.companyDescription')}
          />

          <FormField
            id="taxId"
            name="taxId"
            label={t('merchant.taxId')}
            type="text"
            value={formik.values.taxId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.taxId}
            touched={formik.touched.taxId}
            required
            description={t('prechecksPOS.taxIdDescription')}
          />

          <FormField
            id="creditorId"
            name="creditorId"
            label={t('merchant.creditorId')}
            type="text"
            value={formik.values.creditorId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.creditorId}
            touched={formik.touched.creditorId}
            description={t('prechecksPOS.creditorIdDescription')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              id="industry"
              name="industry"
              label={t('merchant.industry')}
              type="text"
              value={formik.values.industry}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.industry}
              touched={formik.touched.industry}
            />

            <FormField
              id="locations"
              name="locations"
              label={t('merchant.locations')}
              type="text"
              value={formik.values.locations}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.locations}
              touched={formik.touched.locations}
            />
          </div>

          <FormField
            id="address"
            name="address"
            label={t('merchant.address')}
            type="text"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.address}
            touched={formik.touched.address}
            required
            description={t('prechecksPOS.addressDescription')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              id="website"
              name="website"
              label={t('merchant.website')}
              type="url"
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.website}
              touched={formik.touched.website}
            />

            <FormField
              id="director"
              name="director"
              label={t('merchant.director')}
              type="text"
              value={formik.values.director}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.director}
              touched={formik.touched.director}
              required
            />
          </div>
        </SectionHeader>

        <SectionHeader
          title={t('prechecksPOS.contactInformation')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          }
          iconBgColor="bg-green-100"
          iconTextColor="text-green-600"
          titleColor="text-green-600"
        >
          <FormField
            id="contactPerson"
            name="contactPerson"
            label={t('merchant.contactPerson')}
            type="text"
            value={formik.values.contactPerson}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.contactPerson}
            touched={formik.touched.contactPerson}
            required
          />

          <FormField
            id="email"
            name="email"
            label={t('merchant.email')}
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.email}
            touched={formik.touched.email}
            required
          />

          <FormField
            id="telephone"
            name="telephone"
            label={t('merchant.telephone')}
            type="tel"
            value={formik.values.telephone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.telephone}
            touched={formik.touched.telephone}
            required
            description={t('prechecksPOS.telephoneDescription')}
            placeholder="+1 123 456 789"
          />

          <FormField
            id="mobile"
            name="mobile"
            label={t('merchant.mobile')}
            type="tel"
            value={formik.values.mobile}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.mobile}
            touched={formik.touched.mobile}
          />
        </SectionHeader>

        <SectionHeader
          title={t('prechecksPOS.productServiceInformation')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          iconBgColor="bg-purple-100"
          iconTextColor="text-purple-600"
          titleColor="text-purple-600"
        >
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-6">
            <FormCheckboxGroup
              name="cards"
              label={t('prechecksPOS.desiredCardAcceptance')}
              options={[
                { value: 'Visa/Mastercard', label: 'Visa/Mastercard' },
                { value: 'Discover/Diners', label: 'Discover/Diners' },
                { value: 'American Express', label: 'American Express' },
                { value: 'VPAY/Maestro', label: 'VPAY/Maestro' },
                { value: 'Girocard/EC cash', label: 'Girocard/EC Cash' },
                { value: 'China Union Pay', label: 'China Union Pay' },
              ]}
              values={formik.values.cards}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.cards}
              touched={formik.touched.cards}
              required
              icon={
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              }
            />
          </div>

          <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-6">
            <FormCheckboxGroup
              name="additionalServices"
              label={t('prechecksPOS.additionalService')}
              options={[
                { value: 'Cash pooling', label: t('prechecksPOS.cashPooling') },
                { value: 'Online document archiving', label: t('prechecksPOS.onlineDocumentArchiving') },
                { value: 'DCC', label: t('prechecksPOS.dcc') },
                { value: 'PayPerLink', label: t('prechecksPOS.payPerLink') },
              ]}
              values={formik.values.additionalServices}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.additionalServices}
              touched={formik.touched.additionalServices}
              icon={
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            />
          </div>

          <FormField
            id="onlineShop"
            name="onlineShop"
            label={t('merchant.onlineShop')}
            type="url"
            value={formik.values.onlineShop}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.onlineShop}
            touched={formik.touched.onlineShop}
          />
        </SectionHeader>

        <SectionHeader
          title={t('prechecksPOS.furtherInformation')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          iconBgColor="bg-orange-100"
          iconTextColor="text-orange-600"
          titleColor="text-orange-600"
        >
          <FormField
            id="currentProvider"
            name="currentProvider"
            label={t('merchant.currentProvider')}
            type="text"
            value={formik.values.currentProvider}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.currentProvider}
            touched={formik.touched.currentProvider}
            required
            description={t('prechecksPOS.currentProviderDescription')}
          />

          <FormField
            id="turnover"
            name="turnover"
            label={t('prechecksPOS.monthlyCardTurnover')}
            type="text"
            value={formik.values.turnover}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.turnover}
            touched={formik.touched.turnover}
            required
          />

          <FormField
            id="receiptSize"
            name="receiptSize"
            label={t('prechecksPOS.averageReceiptSize')}
            type="text"
            value={formik.values.receiptSize}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.receiptSize}
            touched={formik.touched.receiptSize}
            required
          />

          <FormField
            id="bankStatementReference"
            name="bankStatementReference"
            label={t('prechecksPOS.bankStatementReference')}
            type="text"
            value={formik.values.bankStatementReference}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.bankStatementReference}
            touched={formik.touched.bankStatementReference}
            required
          />

          <FormSelect
            id="terminals"
            name="terminals"
            label={t('prechecksPOS.terminals')}
            value={formik.values.terminals}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            error={formik.errors.terminals}
            touched={formik.touched.terminals}
            required
          />

          <FormSelect
            id="checkoutConnection"
            name="checkoutConnection"
            label={t('prechecksPOS.checkoutConnection')}
            value={formik.values.checkoutConnection}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            options={[
              { value: 'yes', label: t('common.yes') },
              { value: 'no', label: t('common.no') },
            ]}
            error={formik.errors.checkoutConnection}
            touched={formik.touched.checkoutConnection}
            required
          />

          <FormSelect
            id="cashCut"
            name="cashCut"
            label={t('prechecksPOS.cashCut')}
            value={formik.values.cashCut}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            options={[
              { value: 'standard', label: t('prechecksPOS.cashCutStandard') },
              { value: 'detailed', label: t('prechecksPOS.cashCutDetailed') },
            ]}
            error={formik.errors.cashCut}
            touched={formik.touched.cashCut}
            required
          />

          <FormSelect
            id="tip"
            name="tip"
            label={t('prechecksPOS.tipFunction')}
            value={formik.values.tip}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            options={[
              { value: 'no', label: t('common.no') },
              { value: 'signature', label: t('prechecksPOS.tipFunctionSignature') },
              { value: 'without-signature', label: t('prechecksPOS.tipFunctionWithoutSignature') },
            ]}
            error={formik.errors.tip}
            touched={formik.touched.tip}
            required
          />
        </SectionHeader>

        <ErrorSummary errors={formik.errors} title={t('prechecksPOS.pleaseFixTheFollowingErrors')} />

        <SubmitButton label={t('common.submit')} isSubmitting={formik.isSubmitting} />
      </form>
    </>
  );

  return isPublicPath ? (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-brand">
      <div className="flex flex-row flex-nowrap min-h-full justify-center relative">
        <main className="p-4 sm:p-6 md:p-8 w-full max-w-5xl mx-auto">
          <div className="flex justify-center mb-8">
            <BrandLogo brand={brand} className="h-16 w-auto" variant="dark" />
          </div>
          <div className="flex justify-center">
            <div className="w-full">{content}</div>
          </div>
        </main>

        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  ) : (
    <div className="flex justify-center">
      <div className="w-full max-w-5xl">{content}</div>
    </div>
  );
};

export default PrecheckPOSForm;
